import {
  COUNTRY_CURRENCIES,
  CurrencyCode,
  fromMinorUnits,
  toMinorUnits,
} from '@final-commerce/common';
import { RATE_VALIDATION_PATTERN } from '@/constants/constants';
import { PricingConfigurationValues } from '@/services/Pricing/pricing-configuration.service';
import {
  hasDecimalSeparator,
  hasMaxDecimalPlaces,
  normalizeDecimalSeparator,
} from './pricingHelpers';

export type FormRow = {
  code: string;
  variable: string;
  fixed: string;
  amex: string;
};

export type FieldKey = 'variable' | 'fixed' | 'amex';

export const FIELD_KEYS: FieldKey[] = ['variable', 'fixed', 'amex'];

export const onPercentKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
  const target = event.target as HTMLInputElement;
  const currentValue = target.value || '';
  const key = event.key;
  const isTextSelected = (target.selectionStart || 0) !== (target.selectionEnd || 0);

  if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)) return;
  if (isTextSelected) return;
  if (hasMaxDecimalPlaces(currentValue, key)) {
    event.preventDefault();
    return;
  }
  if ((key === ',' || key === '.') && hasDecimalSeparator(currentValue)) {
    event.preventDefault();
    return;
  }
  if (!RATE_VALIDATION_PATTERN.test(key)) {
    event.preventDefault();
  }
};

export const normalizeBlur = (event: React.FocusEvent<HTMLInputElement>) => {
  const value = event.target.value;
  if (!value) return;
  const normalized = normalizeDecimalSeparator(value);
  const formatted = parseFloat(normalized).toFixed(2);
  event.target.value = formatted;
  event.target.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
};

export const formatPercent = (decimal: number): string => (decimal * 100).toFixed(2);

export const fixedMinorToDisplay = (minor: number, countryCode: string): string => {
  const currency = COUNTRY_CURRENCIES[countryCode];
  if (!currency) return minor.toFixed(2);
  return fromMinorUnits(minor, currency as CurrencyCode).toFixed(2);
};

export const fixedDisplayToMinor = (raw: string, countryCode: string): number => {
  const value = parseFloat(normalizeDecimalSeparator(raw));
  if (!Number.isFinite(value)) return 0;
  const currency = COUNTRY_CURRENCIES[countryCode];
  if (!currency) return Math.round(value);
  return toMinorUnits(value, currency as CurrencyCode);
};

export const valuesToFormRows = (values: PricingConfigurationValues): FormRow[] =>
  Object.entries(values).map(([code, v]) => ({
    code,
    variable: formatPercent(v.variable),
    fixed: fixedMinorToDisplay(v.fixed, code),
    amex: formatPercent(v.amex),
  }));

export const formRowsToValues = (rows: FormRow[]): PricingConfigurationValues => {
  const result: PricingConfigurationValues = {};
  for (const row of rows) {
    const variable = parseFloat(normalizeDecimalSeparator(row.variable)) / 100;
    const amex = parseFloat(normalizeDecimalSeparator(row.amex)) / 100;
    result[row.code] = {
      variable: Number.isFinite(variable) ? Number(variable.toFixed(4)) : 0,
      fixed: fixedDisplayToMinor(row.fixed, row.code),
      amex: Number.isFinite(amex) ? Number(amex.toFixed(4)) : 0,
    };
  }
  return result;
};

// Compare bounds at the 2-decimal precision the inputs actually display, so a value
// that equals the rounded default/minimum doesn't fail on sub-precision float noise.
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

export const validateAgainstMinimum = (
  raw: string,
  minimum: number | undefined,
  isPercent: boolean,
  errorKey: string,
): true | string => {
  const entered = parseFloat(normalizeDecimalSeparator(raw));
  if (Number.isNaN(entered)) return errorKey;
  if (minimum === undefined) return entered >= 0 ? true : errorKey;
  const floor = round2(isPercent ? minimum * 100 : minimum);
  return round2(entered) >= floor ? true : errorKey;
};

export const validateAgainstMaximum = (
  raw: string,
  maximum: number | undefined,
  isPercent: boolean,
  errorKey: string,
): true | string => {
  if (maximum === undefined) return true;
  const entered = parseFloat(normalizeDecimalSeparator(raw));
  if (Number.isNaN(entered)) return true;
  const ceiling = round2(isPercent ? maximum * 100 : maximum);
  return round2(entered) <= ceiling ? true : errorKey;
};

export const displayFieldValue = (
  value: number | undefined,
  field: FieldKey,
  countryCode: string,
  fixedSuffix: string,
): string => {
  if (value === undefined) return '—';
  if (field === 'fixed') return `${fixedMinorToDisplay(value, countryCode)} ${fixedSuffix}`.trim();
  return `${formatPercent(value)} %`;
};

export const computeMargin = (
  watched: unknown,
  orgValue: number | undefined,
  field: FieldKey,
  countryCode: string,
  fixedSuffix: string,
): string => {
  if (orgValue === undefined || typeof watched !== 'string' || watched === '') return '—';
  const entered = parseFloat(normalizeDecimalSeparator(watched));
  if (!Number.isFinite(entered)) return '—';
  if (field === 'fixed') {
    const baseline = parseFloat(fixedMinorToDisplay(orgValue, countryCode));
    if (!Number.isFinite(baseline)) return '—';
    return `${(entered - baseline).toFixed(2)} ${fixedSuffix}`.trim();
  }
  return `${(entered - orgValue * 100).toFixed(2)} %`;
};
